import {
  Poppins_400Regular,
  Poppins_500Medium,
  Poppins_600SemiBold,
  Poppins_700Bold,
  useFonts as useGoogleFonts,
} from '@expo-google-fonts/poppins';
import {
  Inter_400Regular,
  Inter_500Medium,
  Inter_600SemiBold,
  Inter_700Bold,
  Inter_800ExtraBold,
} from '@expo-google-fonts/inter';
import * as SplashScreen from 'expo-splash-screen';
import { StatusBar } from 'expo-status-bar';
import { Provider } from 'react-redux';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AppNavigator } from './navigation/AppNavigator';
import { GoogleAuthProvider } from './providers/GoogleAuthProvider';
import { store } from './store/store';
import { useEffect } from 'react';
import { useFonts as useLocalFonts } from 'expo-font';
import Toast, { ErrorToast, SuccessToast } from 'react-native-toast-message';
import { colors } from '../src/styles/theme';
import { clarity } from './utils/clarity';
import { setOnSessionExpired } from './api/remembrApi';
import { logoutUser } from './features/auth/authSlice';
import { bootstrapPendingInviteFromUrl } from './features/sharing/pendingInvite';
import { bootstrapPostBootRedirectFromUrl } from './navigation/deepLinkRedirect';
import Ionicons from '@expo/vector-icons/Ionicons';
import AntDesign from '@expo/vector-icons/AntDesign';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';

// Sprint 2.5 (FE-9.4.5) — Bootstrap the invite-token stash BEFORE the
// React component tree mounts. If the user landed on
// /invite/<token> while unauthenticated, the deep-link config in
// AppNavigator can't match the URL (the route lives inside the
// auth-gated MainApp stack), so the token would otherwise be
// dropped. Stashing it in localStorage at module load means the
// AppNavigator's post-auth boot-resume hook can pick it up after
// the user signs in. Idempotent + safe on SSR / native runtimes.
bootstrapPendingInviteFromUrl();
// FE #199 — Capture the original deep-link target (e.g. /notifications,
// /groups/376, /item/5157) BEFORE the React tree mounts. The
// authenticated routes live inside the gated MainApp stack so React
// Navigation can't match them when isAuthenticated=false; without this
// stash, the user signs in and lands on Home with no context of what
// they were trying to reach. Replay happens post-auth in AppNavigator.
bootstrapPostBootRedirectFromUrl();

export default function App() {
  // Load Google fonts — Inter (Fidelity Purple design system) + Poppins (legacy screens)
  const [googleFontsLoaded] = useGoogleFonts({
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Inter_700Bold,
    Inter_800ExtraBold,
    Poppins_400Regular,
    Poppins_500Medium,
    Poppins_600SemiBold,
    Poppins_700Bold,
  });

  // Load essential local fonts and icon fonts (ensures icons load in production web builds)
  const [localFontsLoaded] = useLocalFonts({
    HollaMediaeval_400Regular: require('../src/fonts/hollamediaeval/HollaMediaeval.ttf'),
    RobotoMono_400Regular: require('../src/fonts/robotomono/RobotoMono-Regular.ttf'),
    ...Ionicons.font,
    ...AntDesign.font,
    ...MaterialIcons.font,
  });

  // Combine both font loading states
  const fontsLoaded = googleFontsLoaded && localFontsLoaded;

  useEffect(() => {
    if (fontsLoaded) {
      SplashScreen.hideAsync();
    }
  }, [fontsLoaded]);

  // Initialize Microsoft Clarity
  useEffect(() => {
    const initializeClarity = async () => {
      try {
        await clarity.initialize();
      } catch (error) {
        console.warn('Failed to initialize Microsoft Clarity:', error);
      }
    };

    initializeClarity();
  }, []);

  // Wire the API's 401 interceptor to dispatch logoutUser(), which
  // flips isAuthenticated to false and lets AppNavigator swap to the
  // auth stack — so an expired session sends the user straight to
  // Login instead of leaving them on a broken authenticated screen.
  useEffect(() => {
    setOnSessionExpired(() => {
      store.dispatch(logoutUser({ callBackend: false }) as any);
    });
    return () => setOnSessionExpired(null);
  }, []);

  if (!fontsLoaded) {
    return null;
  }

  // Custom Toast configuration to match app branding
  const toastConfig = {
    error: ({ text1, text2 }: any) => (
      <ErrorToast
        style={{
          borderLeftColor: colors.error,
          backgroundColor: colors.surface,
          borderWidth: 1,
          borderColor: colors.gray200,
        }}
        contentContainerStyle={{
          backgroundColor: colors.surface,
        }}
        text1Style={{
          fontSize: 16,
          fontWeight: '600',
          color: colors.text,
        }}
        text2Style={{
          fontSize: 12,
          color: colors.textMuted,
          lineHeight: 16,
        }}
        text1={text1}
        text2={text2}
      />
    ),
    success: ({ text1, text2 }: any) => (
      <SuccessToast
        style={{
          borderLeftColor: colors.success,
          backgroundColor: colors.surface,
          borderWidth: 1,
          borderColor: colors.gray200,
        }}
        contentContainerStyle={{
          backgroundColor: colors.surface,
        }}
        text1Style={{
          fontSize: 16,
          fontWeight: '600',
          color: colors.text,
        }}
        text2Style={{
          fontSize: 12,
          color: colors.textMuted,
          lineHeight: 16,
        }}
        text1={text1}
        text2={text2}
      />
    ),
  };

  return (
    <Provider store={store}>
      <GoogleAuthProvider>
        <ErrorBoundary>
          <StatusBar style="dark" />
          <AppNavigator />
          <Toast config={toastConfig} />
        </ErrorBoundary>
      </GoogleAuthProvider>
    </Provider>
  );
}
